import type { EntityStatus } from '@ms/yammer-data-support';
import { getStatus } from '@ms/yammer-data-support';

import type { MultiTenantNetworkOrganizationEntity } from '../../normalization/graphql/normalizeMultiTenantOrganization.types';
import type { MultiTenantOrganizationNetwork } from '../../state/multiTenantOrganizationNetworks/types';
import type { YammerState } from '../../state/types';
import { getExperimentTreatment } from '../experiments/selectors';
import { createFindById } from '../findById';
import { getCurrentNetwork } from '../networks/selectors';
import { getCurrentNetworkId } from '../session/selectors';

/**
 * @deprecated PERFORMANCE This returns an entity object which will be replaced with a new instance and
 * force re-renders whenever an API call updates the entity state. This should be replaced by a
 * `getMultiTenantOrganizationNetworkField` selector.
 */
export const findById = createFindById('multiTenantOrganizationNetworks');

type GetMultiTenantOrganizationConnectionsForHubNetwork = (
  state: YammerState
) => MultiTenantOrganizationNetwork[] | undefined;
/**
 * @deprecated PERFORMANCE This returns a brand new array instance of entity objects on every call! This selector
 * must be cached. It should also be renamed to `getMultiTenantNetworkIds` and return a cached array of ids instead.
 * Then consumers can pass each id to a child component which can call `useMultiTenantOrganizationNetworkField` for
 * the specific fields it requires, and can manage its own memoization to avoid unnecessary re-renders.
 */
export const getMultiTenantNetworks: GetMultiTenantOrganizationConnectionsForHubNetwork = (state) => {
  if (!getIsMtoEnabled(state)) {
    return;
  }

  const fulfilledNetworks = state.multiTenantOrganizationNetworks.fulfilled.byId;

  return Object.values(fulfilledNetworks);
};

type GetMultiTenantOrganizationStatus = (state: YammerState) => EntityStatus;
export const getMultiTenantOrganizationStatus: GetMultiTenantOrganizationStatus = (state) =>
  getStatus(state.multiTenantOrganization);

type GetMultiTenantOrganization = (state: YammerState) => MultiTenantNetworkOrganizationEntity | undefined;
/**
 * @deprecated PERFORMANCE This returns an entity object which will be replaced with a new instance and
 * force re-renders whenever an API call updates the entity state. This should be replaced by a
 * `getMultiTenantOrganizationField` selector.
 */
export const getMultiTenantOrganization: GetMultiTenantOrganization = (state) =>
  state?.multiTenantOrganization?.fulfilled;

type GetIsMtoEnabled = (state: YammerState) => boolean;
export const getIsMtoEnabled: GetIsMtoEnabled = (state) => {
  const isMtoEnabled = () => getExperimentTreatment(state, 'MultiTenantOrganization') === true;

  return isMtoEnabled();
};

type getIsPermalinkDeeplinkEnabled = (state: YammerState) => boolean;
export const getIsPermalinkDeeplinkEnabled: getIsPermalinkDeeplinkEnabled = (state) =>
  getExperimentTreatment(state, 'EnablePermalinkDeeplink') === true;

type GetCurrentMtoHubNetwork = (state: YammerState) => MultiTenantOrganizationNetwork | null | undefined;
/**
 * @deprecated PERFORMANCE This returns an entity object which will be replaced with a new instance and
 * force re-renders whenever an API call updates the entity state. This should be replaced by a
 * `getCurrentMtoHubNetworkField` selector.
 */
export const getCurrentMtoHubNetwork: GetCurrentMtoHubNetwork = (state) =>
  getMultiTenantNetworks(state)?.find((network) => network.isHubNetwork) ?? null;

type GetCurrentMtoNetwork = (state: YammerState) => MultiTenantOrganizationNetwork | null | undefined;
/**
 * @deprecated PERFORMANCE This returns an entity object which will be replaced with a new instance and
 * force re-renders whenever an API call updates the entity state. This should be replaced by a
 * `getCurrentMtoNetworkField` selector.
 */
export const getCurrentMtoNetwork: GetCurrentMtoNetwork = (state) => {
  const { id: currentNetworkId } = getCurrentNetwork(state);

  return findById(state, currentNetworkId) ?? null;
};

type GetIsMtoAvailable = (state: YammerState) => boolean;
export const getIsMtoAvailable: GetIsMtoAvailable = (state) => getMultiTenantOrganization(state) !== undefined;

type GetIsMtoSpokeNetworkAvailable = (state: YammerState) => boolean;
export const getIsMtoSpokeNetworkAvailable: GetIsMtoSpokeNetworkAvailable = (state) => {
  const networks = getMultiTenantNetworks(state);
  if (!networks) {
    return false;
  }

  return networks.some((network) => !network.isHubNetwork);
};

type GetIsCurrentNetworkSpoke = (state: YammerState) => boolean;
export const getIsCurrentNetworkSpoke: GetIsCurrentNetworkSpoke = (state) => {
  const currentNetworkId = getCurrentNetworkId(state);
  const hubNetworkId = getCurrentMtoHubNetwork(state)?.id;

  return currentNetworkId !== hubNetworkId;
};

type GetMultiTenantNetworksCount = (state: YammerState) => number;
export const getMultiTenantNetworksCount: GetMultiTenantNetworksCount = (state) =>
  getMultiTenantNetworks(state)?.length ?? 0;

type GetMultiTenantMemberNetworksCount = (state: YammerState) => number;
export const getMultiTenantMemberNetworksCount: GetMultiTenantMemberNetworksCount = (state) => {
  const mtoNetworksCount = getMultiTenantNetworksCount(state);

  return mtoNetworksCount > 0 ? mtoNetworksCount - 1 : 0;
};

type getCanAccessMultiTenantOrganizationGroups = (state: YammerState) => boolean;
export const getCanAccessMultiTenantOrganizationGroups: getCanAccessMultiTenantOrganizationGroups = (state) =>
  getMultiTenantOrganization(state)?.canAccessMultiTenantOrganizationGroups ?? false;
